import type { IUserProfileService } from "../interface/IUserProfile";
import type { UserProfile, CreateUserRequest, CreateUserResponse, GetAllUsersResponse, UpdateUserRequest, UpdateUserResponse, GetUserCountResponse } from "../../models/userProfile";
import type { IHttpWrapperService } from "../interface/IHttpWrapperService";
import { container, TYPES } from "../inversify.config";
import type { AxiosResponse } from "axios";
import type { ApiResponse } from "../../models/ApiResponse";
import { isSuccessResponse } from "../../models/ApiResponse";


export class UserProfileService implements IUserProfileService {
 private httpService: IHttpWrapperService = container.get(TYPES.IHttpWrapperService);

    async GetUserByEmail(email: string): Promise<UserProfile | null> {
    try {
        const response: AxiosResponse<ApiResponse<UserProfile>> = await this.httpService.getRequest<UserProfile>(`user/getuserbyemail/${email}`);

        if (isSuccessResponse(response.data)) {
            return response.data.data;
        } else {
            // When isSuccess is false, throw error to indicate authentication failure
            const errorMessage = response.data.message || 'User authentication failed';
            throw new Error(errorMessage);
        }
    } catch (error: any) {
        // Check if this is a 400, 401, or 404 error (user not found / no access)
        const status = error?.response?.status;
        const isAccessDenied = status === 400 || status === 401 || status === 404;

        // Also check error message for access denied indicators
        const errorMessage = error?.message || '';
        const hasAccessDeniedMessage = errorMessage.includes('404') ||
                                       errorMessage.includes('400') ||
                                       errorMessage.includes('401') ||
                                       errorMessage.includes('not found') ||
                                       errorMessage.includes('unauthorized');

        if (isAccessDenied || hasAccessDeniedMessage) {
            // Return null to indicate user not found/no access - this will trigger access denied page
            return null;
        }

        // For other errors, throw a generic error
        if (error instanceof Error && error.message.includes('authentication failed')) {
            throw error;
        }
        throw new Error('Unable to authenticate user - please try again');
    }
    }

    async getUserProfile(): Promise<UserProfile> {
        try {
            const response: AxiosResponse<ApiResponse<UserProfile>> = await this.httpService.getRequest<UserProfile>('user/profile/current');
            
            if (isSuccessResponse(response.data)) {
                return response.data.data;
            } else {
                throw new Error(response.data.message || "Failed to fetch user profile");
            }
        } catch (_error) {
            throw new Error("Failed to fetch user profile");
        }
    }

    async createUser(userData: CreateUserRequest): Promise<CreateUserResponse> {
        try {
            const response: AxiosResponse<ApiResponse<CreateUserResponse>> = await this.httpService.postRequest<CreateUserResponse, CreateUserRequest>('user/createuser', userData);
            
            if (isSuccessResponse(response.data)) {
                return response.data.data;
            } else {
                throw new Error(response.data.message || "Failed to create user");
            }
        } catch (error) {
            if (error instanceof Error) {
                throw new Error(`Failed to create user: ${error.message}`);
            }
            throw new Error("Failed to create user");
        }
    }

    async getAllUsers(): Promise<GetAllUsersResponse> {
        try {
            const response: AxiosResponse<ApiResponse<GetAllUsersResponse>> = await this.httpService.getRequest<GetAllUsersResponse>('user/getallusers');
            
            if (isSuccessResponse(response.data)) {
                return response.data.data;
            } else {
                throw new Error(response.data.message || "Failed to fetch users");
            }
        } catch (error) {
            if (error instanceof Error) {
                throw new Error(`Failed to fetch users: ${error.message}`);
            }
            throw new Error("Failed to fetch users");
        }
    }

    async updateUser(email: string, userId: string, userData: UpdateUserRequest): Promise<UpdateUserResponse> {
        try {
            // Ensure userId is included in the payload
            const payload: UpdateUserRequest = {
                ...userData,
                userId: userId
            };
            
            const response: AxiosResponse<ApiResponse<UpdateUserResponse>> = await this.httpService.putRequest<UpdateUserResponse, UpdateUserRequest>(`user/updateuser/${email}`, payload);
            
            if (isSuccessResponse(response.data)) {
                return response.data.data;
            } else {
                throw new Error(response.data.message || "Failed to update user");
            }
        } catch (error) {
            if (error instanceof Error) {
                throw new Error(`Failed to update user: ${error.message}`);
            }
            throw new Error("Failed to update user");
        }
    }

    async getUserCount(): Promise<GetUserCountResponse> {
        try {
            const response: AxiosResponse<ApiResponse<GetUserCountResponse>> = await this.httpService.getRequest<GetUserCountResponse>('user/getuserscount');
            
            if (isSuccessResponse(response.data)) {
                return response.data.data;
            } else {
                throw new Error(response.data.message || "Failed to fetch user count");
            }
        } catch (error) {
            if (error instanceof Error) {
                throw new Error(`Failed to fetch user count: ${error.message}`);
            }
            throw new Error("Failed to fetch user count");
        }
    }

    async searchUsers(searchText: string, maxItem: number = 20): Promise<UserProfile[]> {
        try {
            const response: AxiosResponse<ApiResponse<UserProfile[]>> = await this.httpService.getRequest<UserProfile[]>(
                `user/searchusers?searchText=${encodeURIComponent(searchText)}&maxItem=${maxItem}`
            );
            
            if (isSuccessResponse(response.data)) {
                return response.data.data;
            } else {
                throw new Error(response.data.message || "Failed to search users");
            }
        } catch (error) {
            if (error instanceof Error) {
                throw new Error(`Failed to search users: ${error.message}`);
            }
            throw new Error("Failed to search users");
        }
    }

}
